import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Eye, X, TextAa, CircleHalf, HandTap,
  ArrowClockwise, LinkSimple, Play,
} from "@phosphor-icons/react";

/**
 * AS TRAINING — Accessibility Widget
 *
 * Lightweight, self-contained. No third-party deps. Provides:
 *   - Font size scaling (100% → 150%)
 *   - High-contrast mode
 *   - Reduced motion (respects OS setting by default)
 *   - Underlined links (helps color-blind users)
 *   - Pause animations
 *
 * Settings persist in localStorage under `as-a11y-settings`.
 * Toggle button is a fixed FAB in the bottom-right corner.
 */

const STORAGE_KEY = "as-a11y-settings";
const DEFAULT_SETTINGS = {
  fontScale: 1,       // 1 | 1.1 | 1.25 | 1.5
  highContrast: false,
  reduceMotion: false,
  underlineLinks: false,
  pauseAnimations: false,
};

const FONT_STEPS = [
  { value: 1,    label: "A",   size: 14 },
  { value: 1.1,  label: "A",   size: 16 },
  { value: 1.25, label: "A",   size: 18 },
  { value: 1.5,  label: "A",   size: 22 },
];

function loadSettings() {
  if (typeof window === "undefined") return DEFAULT_SETTINGS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULT_SETTINGS, ...JSON.parse(raw) } : DEFAULT_SETTINGS;
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function applySettings(s) {
  const root = document.documentElement;
  root.style.setProperty("--a11y-font-scale", s.fontScale);
  root.dataset.a11yHighContrast = s.highContrast ? "true" : "false";
  root.dataset.a11yReduceMotion = s.reduceMotion ? "true" : "false";
  root.dataset.a11yUnderlineLinks = s.underlineLinks ? "true" : "false";
  root.dataset.a11yPauseAnimations = s.pauseAnimations ? "true" : "false";
}

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(loadSettings);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);

  // Apply + persist on every change
  useEffect(() => {
    applySettings(settings);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(settings)); } catch {}
  }, [settings]);

  // Close on Escape and focus management
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); triggerRef.current?.focus(); } };
    document.addEventListener("keydown", onKey);
    // Focus first control on open
    const first = panelRef.current?.querySelector("button");
    first?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && !triggerRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const update = useCallback((patch) => setSettings((s) => ({ ...s, ...patch })), []);
  const reset = () => setSettings(DEFAULT_SETTINGS);

  return createPortal(
    <>
      <button
        ref={triggerRef}
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Cerrar menú de accesibilidad" : "Abrir menú de accesibilidad"}
        aria-expanded={open}
        aria-controls="a11y-panel"
        className="fixed z-[95] bottom-4 left-4 w-12 h-12 rounded-full bg-as-green text-as-black shadow-2xl flex items-center justify-center hover:scale-105 transition-transform focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white"
        data-testid="a11y-widget-toggle"
      >
        <Eye size={24} weight="bold" aria-hidden="true" />
      </button>

      {open && (
        <div
          ref={panelRef}
          id="a11y-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="a11y-panel-title"
          className="fixed z-[95] bottom-20 left-4 w-[300px] max-w-[calc(100vw-2rem)] bg-as-black border-2 border-as-green shadow-2xl"
          data-testid="a11y-widget-panel"
        >
          <div className="flex items-center justify-between p-4 border-b border-as-border">
            <h2 id="a11y-panel-title" className="as-label text-as-green flex items-center gap-2 !text-[11px]">
              <Eye size={14} weight="bold" aria-hidden="true" />
              Accesibilidad
            </h2>
            <button
              onClick={() => { setOpen(false); triggerRef.current?.focus(); }}
              aria-label="Cerrar menú de accesibilidad"
              className="w-7 h-7 flex items-center justify-center text-as-gray hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-as-green"
              data-testid="a11y-widget-close"
            >
              <X size={14} weight="bold" aria-hidden="true" />
            </button>
          </div>

          <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Font size */}
            <section aria-labelledby="a11y-font-title">
              <p id="a11y-font-title" className="as-label !text-[10px] mb-2 text-white">
                <TextAa size={12} weight="bold" aria-hidden="true" className="inline mr-1.5" />
                Tamaño de texto
              </p>
              <div className="grid grid-cols-4 gap-1.5" role="group" aria-label="Nivel de tamaño de texto">
                {FONT_STEPS.map((step) => (
                  <button
                    key={step.value}
                    onClick={() => update({ fontScale: step.value })}
                    aria-pressed={settings.fontScale === step.value}
                    aria-label={`Escala de texto ${Math.round(step.value * 100)} por ciento`}
                    className={`py-2 border font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-as-green ${
                      settings.fontScale === step.value
                        ? "bg-as-green text-as-black border-as-green"
                        : "border-as-border text-white hover:border-as-green"
                    }`}
                    style={{ fontSize: step.size }}
                    data-testid={`a11y-font-${step.value}`}
                  >
                    {step.label}
                  </button>
                ))}
              </div>
            </section>

            <ToggleRow
              testid="a11y-toggle-contrast"
              icon={CircleHalf}
              label="Alto contraste"
              hint="Aumenta el contraste de textos y bordes"
              value={settings.highContrast}
              onChange={(v) => update({ highContrast: v })}
            />
            <ToggleRow
              testid="a11y-toggle-motion"
              icon={HandTap}
              label="Reducir movimiento"
              hint="Desactiva animaciones y transiciones"
              value={settings.reduceMotion}
              onChange={(v) => update({ reduceMotion: v })}
            />
            <ToggleRow
              testid="a11y-toggle-links"
              icon={LinkSimple}
              label="Subrayar enlaces"
              hint="Marca todos los enlaces con subrayado"
              value={settings.underlineLinks}
              onChange={(v) => update({ underlineLinks: v })}
            />
            <ToggleRow
              testid="a11y-toggle-pause"
              icon={Play}
              label="Pausar animaciones auto"
              hint="Detiene autoplay de videos y GIFs"
              value={settings.pauseAnimations}
              onChange={(v) => update({ pauseAnimations: v })}
            />

            <div className="pt-2 border-t border-as-border">
              <button
                onClick={reset}
                className="w-full flex items-center justify-center gap-2 py-2 border border-as-border text-as-gray hover:border-as-green hover:text-as-green text-xs font-bold uppercase tracking-widest transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-as-green"
                data-testid="a11y-reset"
              >
                <ArrowClockwise size={12} weight="bold" aria-hidden="true" />
                Restablecer
              </button>
            </div>

            <p className="text-[10px] text-as-gray-dim leading-relaxed pt-2 border-t border-as-border">
              Este sitio cumple con WCAG 2.1 nivel AA. Si encuentras algún problema, escríbenos a <a href="mailto:astraining2020@gmail.com" className="text-as-green">astraining2020@gmail.com</a>.
            </p>
          </div>
        </div>
      )}
    </>,
    document.body
  );
}

function ToggleRow({ icon: Icon, label, hint, value, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-start gap-2 min-w-0 flex-1">
        <Icon size={16} weight="duotone" color="#39FF14" aria-hidden="true" className="mt-0.5 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-white text-xs font-bold">{label}</p>
          <p className="text-[10px] text-as-gray leading-snug">{hint}</p>
        </div>
      </div>
      <button
        onClick={() => onChange(!value)}
        role="switch"
        aria-checked={value}
        aria-label={`${label}: ${value ? "activado" : "desactivado"}`}
        className={`relative w-10 h-5 rounded-full flex-shrink-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-as-green ${
          value ? "bg-as-green" : "bg-as-border"
        }`}
        data-testid={testid}
      >
        <span
          className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
            value ? "translate-x-5" : "translate-x-0.5"
          }`}
          aria-hidden="true"
        />
      </button>
    </div>
  );
}
